import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";
import { Zap, Euro, TrendingUp, Landmark, Sun, Percent, Calendar, Building2, Home as HomeIcon, Trees, Factory } from "lucide-react";

const DEFAULT_CITIES = ["Roma", "Milano", "Napoli", "Torino", "Firenze", "Bologna", "Palermo", "Bari", "Venezia", "Genova"];

const INSTALLATION_META = {
  residential: {
    label_it: "Residenziale",
    label_en: "Residential",
    subtitle_it: "Casa · 3-20 kWp",
    subtitle_en: "Home · 3-20 kWp",
    icon: HomeIcon,
    color: "#8BA892",
  },
  industrial: {
    label_it: "Industriale",
    label_en: "Industrial",
    subtitle_it: "Capannone · 20-500 kWp",
    subtitle_en: "Warehouse · 20-500 kWp",
    icon: Factory,
    color: "#EAB308",
  },
  ground: {
    label_it: "Terreno agricolo",
    label_en: "Agricultural land",
    subtitle_it: "Agrivoltaico · 20-500 kWp",
    subtitle_en: "Agrivoltaic · 20-500 kWp",
    icon: Trees,
    color: "#166534",
  },
  utility: {
    label_it: "Utility-scale",
    label_en: "Utility-scale",
    subtitle_it: "1 MW+ · investitori",
    subtitle_en: "1 MW+ · investors",
    icon: Building2,
    color: "#17301C",
  },
};

export default function Investment() {
  const { lang } = useApp();
  const isIT = lang === "it";
  const [cities, setCities] = useState(DEFAULT_CITIES);
  const [presets, setPresets] = useState(null);
  const [form, setForm] = useState({
    installation_type: "residential",
    kwp: 6,
    location: "Bari",
    orientation: "south",
    ppa_price_eur_per_kwh: 0.09,
    self_consumption_pct: 70,
    retail_price_eur_per_kwh: 0.28,
    system_cost_per_kw: 1800,
    opex_per_kwp_year: 15,
    lifespan_years: 25,
    degradation_pct_per_year: 0.5,
    financing_share_pct: 0,
    loan_rate_pct: 5,
    loan_years: 10,
    tax_deduction_pct: 50,
    tax_deduction_years: 10,
    depreciation_pct_per_year: 0,
    depreciation_years: 11,
    corporate_tax_rate_pct: 27.9,
    industry_40_credit_pct: 0,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    api.get("/cities").then(({ data }) => Array.isArray(data) && setCities(data)).catch(() => {});
    api.get("/investment/presets").then(({ data }) => setPresets(data?.presets || null)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickInstallationType = (type) => {
    if (!presets || !presets[type]) return;
    const p = presets[type];
    setForm((f) => ({
      ...f,
      installation_type: type,
      kwp: p.kwp,
      system_cost_per_kw: p.system_cost_per_kw,
      opex_per_kwp_year: p.opex_per_kwp_year,
      self_consumption_pct: p.self_consumption_pct,
      retail_price_eur_per_kwh: p.retail_price_eur_per_kwh,
      ppa_price_eur_per_kwh: p.ppa_price_eur_per_kwh,
      tax_deduction_pct: p.tax_deduction_pct,
      tax_deduction_years: p.tax_deduction_years,
      depreciation_pct_per_year: p.depreciation_pct_per_year || 0,
      depreciation_years: p.depreciation_years || 11,
      corporate_tax_rate_pct: p.corporate_tax_rate_pct || 27.9,
      industry_40_credit_pct: p.industry_40_credit_pct || 0,
      lifespan_years: p.lifespan_years,
    }));
    setResult(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/investment/estimate", form);
      setResult(data);
      setTimeout(() => document.getElementById("inv-results")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch { toast.error("Calculation error"); }
    finally { setLoading(false); }
  };

  const cashflowData = result?.cashflow?.map((r) => ({
    year: r.year, net: r.net_eur, cum: r.cumulative_net_eur,
  })) || [];

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="mb-8 max-w-3xl">
          <div className="label-eco mb-3"><Building2 size={12} className="inline mr-1" /> {isIT ? "Investimento fotovoltaico" : "Solar Investment"}</div>
          <h1 className="text-5xl font-black tracking-tight text-[#17301C]">
            {isIT ? "Quanto rende un impianto" : "How much does a plant yield"} <span className="italic font-light text-[#EAB308]">{isIT ? "in 25 anni?" : "in 25 years?"}</span>
          </h1>
          <p className="text-lg text-[#3A4A3E] mt-3">
            {isIT
              ? "Costo, produzione, guadagno reale — per casa, capannone o terreno agricolo. Include detrazione fiscale 50% (Bonus Casa) e finanziamento."
              : "Cost, production, real earnings — for home, warehouse or agricultural land. Includes 50% tax deduction and financing."}
          </p>
        </div>

        {/* Installation type selector - big 4-way toggle */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="installation-type-toggle">
          {Object.entries(INSTALLATION_META).map(([type, meta]) => {
            const Icon = meta.icon;
            const active = form.installation_type === type;
            return (
              <button
                type="button"
                key={type}
                onClick={() => pickInstallationType(type)}
                data-testid={`inv-type-${type}`}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                  active
                    ? "bg-[#17301C] border-[#17301C] text-white shadow-lg scale-[1.02]"
                    : "bg-white border-[#DDE0D8] hover:border-[#17301C] text-[#17301C]"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0`} style={{ background: active ? meta.color : `${meta.color}22` }}>
                    <Icon size={18} className={active ? "text-white" : ""} style={{ color: active ? "white" : meta.color }} />
                  </div>
                  <div className="min-w-0">
                    <div className={`font-black text-sm truncate ${active ? "text-white" : "text-[#17301C]"}`}>{isIT ? meta.label_it : meta.label_en}</div>
                    <div className={`text-[10px] font-semibold uppercase tracking-widest ${active ? "text-[#EAB308]" : "text-[#6E7A71]"}`}>
                      {isIT ? meta.subtitle_it : meta.subtitle_en}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <form onSubmit={submit} className="md:col-span-5 card-surface p-8 space-y-5" data-testid="investment-form">
            <Section title={isIT ? "Impianto" : "Plant"}>
              <FormField label={isIT ? "Potenza (kWp)" : "Capacity (kWp)"} icon={Zap}>
                <input data-testid="inv-kwp" type="number"
                  min={form.installation_type === "residential" ? "1" : "10"}
                  step={form.installation_type === "residential" ? "0.5" : "10"}
                  required value={form.kwp}
                  onChange={(e) => update("kwp", parseFloat(e.target.value))} className="inp" />
                <div className="text-xs text-[#6E7A71] mt-1 font-mono-nums">
                  {form.kwp >= 100
                    ? <>= {(form.kwp / 1000).toFixed(2)} MW · ~{Math.ceil(form.kwp * 1000 / 550)} pannelli</>
                    : <>~{Math.ceil(form.kwp * 1000 / 450)} pannelli da 450W</>}
                </div>
              </FormField>
              <FormField label={isIT ? "Località" : "Location"} icon={Sun}>
                <input
                  data-testid="inv-location"
                  list="inv-cities-datalist"
                  value={form.location}
                  onChange={(e) => update("location", e.target.value)}
                  placeholder={isIT ? "Città o comune (es. Rocca di Neto)" : "City or town"}
                  className="inp"
                  autoComplete="off"
                />
                <datalist id="inv-cities-datalist">
                  {cities.map((c) => <option key={c} value={c} />)}
                </datalist>
              </FormField>
            </Section>

            <Section title={isIT ? "Economia" : "Economics"}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label={isIT ? "Costo (€/kWp)" : "Cost (€/kWp)"}>
                  <input data-testid="inv-cost-per-kw" type="number" step="50" required value={form.system_cost_per_kw}
                    onChange={(e) => update("system_cost_per_kw", parseFloat(e.target.value))} className="inp" />
                </FormField>
                <FormField label={isIT ? "PPA (€/kWh)" : "PPA (€/kWh)"}>
                  <input data-testid="inv-ppa" type="number" step="0.01" required value={form.ppa_price_eur_per_kwh}
                    onChange={(e) => update("ppa_price_eur_per_kwh", parseFloat(e.target.value))} className="inp" />
                </FormField>
                <FormField label={isIT ? "OPEX (€/kWp/anno)" : "OPEX (€/kWp/y)"}>
                  <input data-testid="inv-opex" type="number" step="1" required value={form.opex_per_kwp_year}
                    onChange={(e) => update("opex_per_kwp_year", parseFloat(e.target.value))} className="inp" />
                </FormField>
                <FormField label={isIT ? "Autoconsumo %" : "Self-cons %"}>
                  <input data-testid="inv-self" type="number" step="1" min="0" max="100" required value={form.self_consumption_pct}
                    onChange={(e) => update("self_consumption_pct", parseFloat(e.target.value))} className="inp" />
                </FormField>
              </div>
            </Section>

            <Section title={isIT ? "Detrazione fiscale" : "Tax deduction"}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label={isIT ? "% Detrazione" : "% Deduction"}>
                  <input data-testid="inv-tax-pct" type="number" step="1" min="0" max="100" value={form.tax_deduction_pct}
                    onChange={(e) => update("tax_deduction_pct", parseFloat(e.target.value) || 0)} className="inp" />
                  <div className="text-[10px] text-[#6E7A71] mt-1">
                    {form.installation_type === "residential"
                      ? (isIT ? "Bonus Casa 50% (fino 31/12/2025), poi 36%" : "Home Bonus 50% (until 31/12/2025), then 36%")
                      : (isIT ? "0 per aziende (vedi Ammortamento sotto)" : "0 for businesses (see Depreciation below)")}
                  </div>
                </FormField>
                <FormField label={isIT ? "Anni ripartizione" : "Split years"}>
                  <input data-testid="inv-tax-years" type="number" step="1" min="1" max="15" value={form.tax_deduction_years}
                    onChange={(e) => update("tax_deduction_years", parseInt(e.target.value) || 10)} className="inp" />
                </FormField>
              </div>
            </Section>

            {form.installation_type !== "residential" && (
              <Section title={isIT ? "Ammortamento aziendale" : "Corporate depreciation"}>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label={isIT ? "% Ammortamento/anno" : "% Depreciation/yr"}>
                    <input data-testid="inv-depr-pct" type="number" step="0.5" min="0" max="20" value={form.depreciation_pct_per_year}
                      onChange={(e) => update("depreciation_pct_per_year", parseFloat(e.target.value) || 0)} className="inp" />
                    <div className="text-[10px] text-[#6E7A71] mt-1">
                      {isIT ? "9% standard fotovoltaico (categoria impianti generici)" : "9% standard PV (generic plant category)"}
                    </div>
                  </FormField>
                  <FormField label={isIT ? "Aliquota IRES+IRAP %" : "IRES+IRAP rate %"}>
                    <input data-testid="inv-corp-tax" type="number" step="0.1" min="0" max="60" value={form.corporate_tax_rate_pct}
                      onChange={(e) => update("corporate_tax_rate_pct", parseFloat(e.target.value) || 0)} className="inp" />
                    <div className="text-[10px] text-[#6E7A71] mt-1">27.9% = IRES 24% + IRAP 3.9%</div>
                  </FormField>
                  <FormField label={isIT ? "% Anni ammortamento" : "Depreciation years"}>
                    <input data-testid="inv-depr-years" type="number" step="1" min="1" max="25" value={form.depreciation_years}
                      onChange={(e) => update("depreciation_years", parseInt(e.target.value) || 11)} className="inp" />
                  </FormField>
                  <FormField label={isIT ? "% Credito Industria 4.0" : "% Industry 4.0 credit"}>
                    <input data-testid="inv-ind40-pct" type="number" step="1" min="0" max="50" value={form.industry_40_credit_pct}
                      onChange={(e) => update("industry_40_credit_pct", parseFloat(e.target.value) || 0)} className="inp" />
                    <div className="text-[10px] text-[#6E7A71] mt-1">
                      {isIT ? "5% Legge Bilancio 2025 (una tantum su 3 anni)" : "5% 2025 Budget Law (one-time over 3 years)"}
                    </div>
                  </FormField>
                </div>
              </Section>
            )}

            <Section title={isIT ? "Finanziamento (opzionale)" : "Financing (optional)"}>
              <div className="grid grid-cols-3 gap-3">
                <FormField label={isIT ? "% Prestito" : "% Loan"}>
                  <input data-testid="inv-financing" type="number" step="5" min="0" max="100" value={form.financing_share_pct}
                    onChange={(e) => update("financing_share_pct", parseFloat(e.target.value))} className="inp" />
                </FormField>
                <FormField label={isIT ? "Tasso %" : "Rate %"}>
                  <input data-testid="inv-rate" type="number" step="0.1" min="0" value={form.loan_rate_pct}
                    onChange={(e) => update("loan_rate_pct", parseFloat(e.target.value))} className="inp" />
                </FormField>
                <FormField label={isIT ? "Durata anni" : "Term years"}>
                  <input data-testid="inv-loan-years" type="number" step="1" min="1" max="25" value={form.loan_years}
                    onChange={(e) => update("loan_years", parseInt(e.target.value))} className="inp" />
                </FormField>
              </div>
            </Section>

            <button data-testid="inv-submit" type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-60">
              {loading ? "..." : isIT ? "Calcola investimento" : "Calculate investment"}
            </button>
          </form>

          <div className="md:col-span-7">
            {!result ? (
              <div className="card-surface p-12 h-full flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 rounded-full bg-[#EAB308] flex items-center justify-center mb-4">
                  <Landmark size={28} className="text-[#17301C]" />
                </div>
                <p className="text-[#6E7A71]">
                  {isIT ? "Compila il form per vedere ROI, payback e cashflow a 20 anni." : "Fill the form to see ROI, payback and 20-year cashflow."}
                </p>
              </div>
            ) : (
              <motion.div id="inv-results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="inv-results">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <KPI icon={Euro} label={isIT ? "CAPEX" : "CAPEX"} value={`€ ${(result.kpi.capex_eur / 1000).toLocaleString(undefined, {maximumFractionDigits: 0})}k`} accent="#EAB308" />
                  {(result.kpi.total_tax_credit_eur > 0 || result.kpi.total_depreciation_tax_benefit_eur > 0 || result.kpi.total_industry_40_credit_eur > 0) && (
                    <KPI icon={Percent} label={isIT ? "Netto dopo fisco" : "Net after tax"} value={`€ ${(result.kpi.net_capex_eur / 1000).toLocaleString(undefined, {maximumFractionDigits: 0})}k`} accent="#8BA892" />
                  )}
                  <KPI icon={Zap} label={isIT ? "Produzione anno 1" : "Year 1 production"} value={`${(result.kpi.annual_production_kwh_y1 / 1000).toLocaleString(undefined, {maximumFractionDigits: 0})}k`} unit="kWh" />
                  <KPI icon={TrendingUp} label={isIT ? "Ricavo anno 1" : "Year 1 revenue"} value={`€ ${(result.kpi.annual_revenue_eur_y1).toLocaleString(undefined, {maximumFractionDigits: 0})}`} />
                  <KPI icon={Percent} label={`ROI ${result.input.lifespan_years}y`} value={`${result.kpi.roi_pct_lifetime}%`} accent="#8BA892" />
                  <KPI icon={Percent} label="IRR ~" value={`${result.kpi.irr_approx_pct}%`} />
                  <KPI icon={Calendar} label={isIT ? "Rientro" : "Payback"} value={result.kpi.payback_years} unit={isIT ? "anni" : "y"} />
                </div>

                {result.kpi.total_tax_credit_eur > 0 && (
                  <div className="card-surface p-4 bg-[#EAB308]/10 border-l-4 border-[#EAB308]">
                    <div className="text-xs uppercase tracking-widest font-black text-[#8B7000] mb-1">
                      {isIT ? "💰 Detrazione fiscale totale" : "💰 Total tax credit"}
                    </div>
                    <div className="text-2xl font-black font-mono-nums text-[#17301C]">
                      € {result.kpi.total_tax_credit_eur.toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </div>
                    <div className="text-xs text-[#3A4A3E] mt-1">
                      {isIT
                        ? `Recuperi € ${(result.kpi.annual_tax_credit_eur).toLocaleString(undefined, {maximumFractionDigits: 0})}/anno per ${result.input.tax_deduction_years} anni tramite Bonus Casa/IRPEF.`
                        : `You get back € ${(result.kpi.annual_tax_credit_eur).toLocaleString(undefined, {maximumFractionDigits: 0})}/year for ${result.input.tax_deduction_years} years via Home Bonus.`}
                    </div>
                  </div>
                )}

                {(result.kpi.total_depreciation_tax_benefit_eur > 0 || result.kpi.total_industry_40_credit_eur > 0) && (
                  <div className="card-surface p-4 bg-[#8BA892]/10 border-l-4 border-[#166534]" data-testid="depreciation-card">
                    <div className="text-xs uppercase tracking-widest font-black text-[#166534] mb-2">
                      {isIT ? "🏭 Vantaggi fiscali aziendali" : "🏭 Corporate tax benefits"}
                    </div>
                    {result.kpi.total_depreciation_tax_benefit_eur > 0 && (
                      <div className="mb-2">
                        <div className="flex items-baseline justify-between gap-4">
                          <div className="text-sm font-bold text-[#17301C]">
                            {isIT ? "Ammortamento fiscale " : "Tax depreciation "}
                            ({result.input.depreciation_pct_per_year}%/anno × {result.input.corporate_tax_rate_pct}% aliquota × {result.input.depreciation_years} anni)
                          </div>
                          <div className="text-lg font-black font-mono-nums text-[#166534] whitespace-nowrap">
                            € {result.kpi.total_depreciation_tax_benefit_eur.toLocaleString(undefined, {maximumFractionDigits: 0})}
                          </div>
                        </div>
                        <div className="text-[11px] text-[#3A4A3E]">
                          {isIT
                            ? `Risparmio fiscale annuale ~€${result.kpi.annual_depreciation_tax_benefit_eur.toLocaleString(undefined, {maximumFractionDigits: 0})}`
                            : `Annual tax saving ~€${result.kpi.annual_depreciation_tax_benefit_eur.toLocaleString(undefined, {maximumFractionDigits: 0})}`}
                        </div>
                      </div>
                    )}
                    {result.kpi.total_industry_40_credit_eur > 0 && (
                      <div>
                        <div className="flex items-baseline justify-between gap-4">
                          <div className="text-sm font-bold text-[#17301C]">
                            {isIT ? "Credito Industria 4.0" : "Industry 4.0 credit"}
                            &nbsp;({result.input.industry_40_credit_pct}% CAPEX)
                          </div>
                          <div className="text-lg font-black font-mono-nums text-[#166534] whitespace-nowrap">
                            € {result.kpi.total_industry_40_credit_eur.toLocaleString(undefined, {maximumFractionDigits: 0})}
                          </div>
                        </div>
                        <div className="text-[11px] text-[#3A4A3E]">
                          {isIT
                            ? `Ripartito su 3 anni (Legge Bilancio 2025)`
                            : `Split over 3 years (2025 Budget Law)`}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="card-surface p-6 bg-[#17301C] text-white border-[#17301C]">
                  <div className="text-xs uppercase tracking-widest font-bold text-[#EAB308] mb-2">
                    {isIT ? "Profitto netto totale" : "Total net profit"} · {result.input.lifespan_years} {isIT ? "anni" : "years"}
                  </div>
                  <div className="text-5xl font-black font-mono-nums text-[#EAB308]">
                    € {result.kpi.total_profit_eur.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </div>
                  <div className="text-sm text-white/70 mt-3">
                    {isIT ? "CO₂ evitata totale:" : "Total CO₂ saved:"} <span className="font-mono-nums font-bold text-white">{result.kpi.co2_saved_tonnes_lifetime.toLocaleString()} t</span>
                  </div>
                </div>

                <div className="card-surface p-6">
                  <div className="label-eco mb-3">{isIT ? "Cashflow cumulativo" : "Cumulative cashflow"}</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={cashflowData}>
                      <defs>
                        <linearGradient id="cf" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#EAB308" stopOpacity={0.6} />
                          <stop offset="100%" stopColor="#EAB308" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#DDE0D8" />
                      <XAxis dataKey="year" stroke="#6E7A71" fontSize={11} />
                      <YAxis stroke="#6E7A71" fontSize={11} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #DDE0D8", borderRadius: 8 }}
                        formatter={(v) => `€ ${v.toLocaleString(undefined, {maximumFractionDigits: 0})}`} />
                      <Area dataKey="cum" stroke="#17301C" strokeWidth={2} fill="url(#cf)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="card-surface p-6">
                  <div className="label-eco mb-3">{isIT ? "Utile netto annuo (€)" : "Annual net profit (€)"}</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={cashflowData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#DDE0D8" />
                      <XAxis dataKey="year" stroke="#6E7A71" fontSize={11} />
                      <YAxis stroke="#6E7A71" fontSize={11} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #DDE0D8", borderRadius: 8 }}
                        formatter={(v) => `€ ${v.toLocaleString(undefined, {maximumFractionDigits: 0})}`} />
                      <Line dataKey="net" stroke="#EAB308" strokeWidth={2.5} dot={{ fill: "#17301C", r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
      <style>{`.inp { width: 100%; padding: 0.65rem 0.75rem; border-radius: 0.5rem; border: 1px solid #DDE0D8; background: white; font-size: 0.925rem; transition: border-color .2s; }
      .inp:focus { outline: none; border-color: #17301C; box-shadow: 0 0 0 1px #17301C; }`}</style>
    </div>
  );
}

const FormField = ({ label, icon: Icon, children }) => (
  <div>
    <div className="label-eco mb-2 flex items-center gap-1 text-[10px]">
      {Icon && <Icon size={12} />} {label}
    </div>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-xs font-black uppercase tracking-widest text-[#EAB308] mb-3 pb-2 border-b border-[#DDE0D8]">{title}</div>
    <div className="space-y-3">{children}</div>
  </div>
);

const KPI = ({ icon: Icon, label, value, unit, accent }) => (
  <div className="card-surface p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: accent || "#EAE7E0" }}>
        <Icon size={14} className="text-[#17301C]" />
      </div>
      <div className="label-eco text-[10px] text-[#6E7A71]">{label}</div>
    </div>
    <div className="text-2xl font-black font-mono-nums text-[#17301C]">{value}<span className="text-xs ml-1 font-medium text-[#6E7A71]">{unit}</span></div>
  </div>
);
